import type { CompanyResponse, LicenceResponse, ListResponse } from '@shared/schemas';

import { createRecordingGateway, type RecordingOptions } from '@/utils/recording-gateway';

import { type EnrichmentStats, enrichmentStatsSchema } from '../../_shared/domain/stats.domain';
import type { EnrichmentCandidate } from '../domain/candidates.domain';
import { type EnrichmentSession, enrichmentSessionSchema } from '../domain/sessions.domain';
import type { SessionsGateway } from './sessions.gateway';

interface InMemoryOptions extends RecordingOptions<SessionsGateway> {
  sessions?: EnrichmentSession[];
  stats?: EnrichmentStats;
  licences?: LicenceResponse[];
  profileTypes?: string[];
  lists?: ListResponse[];
  candidates?: EnrichmentCandidate[];
  companies?: CompanyResponse[];
  /** Session returned by createSession (defaults to a minimal PENDING stub). */
  createdSession?: EnrichmentSession;
}

const EMPTY_STATS: EnrichmentStats = enrichmentStatsSchema.parse({});

const STUB_SESSION: EnrichmentSession = enrichmentSessionSchema.parse({
  id: 'new-session-id',
  type: 'linkedin_standard',
  status: 'PENDING',
  created_at: '2026-01-01T00:00:00.000Z',
});

/**
 * Deterministic in-memory gateway for tests. Records every call and lets a test
 * force a failure or hang per method (via the shared `createRecordingGateway`),
 * so the saga's success / error / cancellation branches run without a network.
 */
export function createInMemoryGateway(options: InMemoryOptions = {}) {
  return createRecordingGateway<SessionsGateway>(
    {
      fetchSessions: () => options.sessions ?? [],
      fetchStats: () => options.stats ?? EMPTY_STATS,
      createSession: () => options.createdSession ?? STUB_SESSION,
      fetchLicences: () => options.licences ?? [],
      fetchProfileTypes: () => options.profileTypes ?? [],
      fetchLists: () => options.lists ?? [],
      fetchCandidates: () => options.candidates ?? [],
      fetchCompanies: () => options.companies ?? [],
    },
    options,
  );
}
